/* eslint-disable no-useless-escape */
import { emptyCart, priceListAddToCart } from 'redux/actions/cartActions'
import { getNextLineNum } from 'Utils/Helpers/getNextLineNum'
import { fetchQuotePDF } from 'redux/actions/cotizacionActions'
import {
  COST_EXEMPT_PRODUCT_TYPE_IDS,
  companiesId,
  constFormatterCurrency,
  CUSTOMERID,
  documentTypeId,
  DUMMY_SKU_LITA,
  ROUTES,
  salesRoles,
  searchTitle,
} from './constants'
import {
  addUpdateSalesDocuments,
  listODCDocument,
  searchSalesDocumentDetail,
} from 'api/mdl11'
import { fetchPriceInventory } from 'redux/actions/priceProductsAction'
import i18n from '../i18n'
import { openUrlInNewTab } from './openUrlInNewTab'

export const isCostExemptProduct = item =>
  COST_EXEMPT_PRODUCT_TYPE_IDS.includes(item?.ProductTypeId?.toUpperCase())

/**
 * Verifica si un producto corresponde al SKU dummy configurado para LITA.
 * Este producto temporal requiere validación antes de despacho.
 * @param {Object} product - Producto o fila de Orden de Venta
 * @returns {boolean} - true si el producto es el SKU dummy de LITA
 */
export const isDummyLitaProduct = product => {
  return (
    product?.Sku === DUMMY_SKU_LITA ||
    product?.SystemCode === DUMMY_SKU_LITA ||
    product?.referencia === DUMMY_SKU_LITA
  )
}

/**
 * Cuenta la cantidad de productos dummy de LITA en un arreglo.
 * @param {Array} products - Lista de productos
 * @returns {number} - Cantidad de productos dummy encontrados
 */
export const countDummyLitaProducts = products => {
  if (!Array.isArray(products)) return 0
  return products.filter(isDummyLitaProduct).length
}

/**
 * Verifica si algún producto en una lista es un SKU dummy de LITA.
 * @param {Array} products - Lista de productos
 * @returns {boolean} - true si al menos un producto es dummy
 */
export const hasDummyLitaProducts = products => {
  if (!Array.isArray(products)) return false
  return products.some(isDummyLitaProduct)
}

/**
 * Obtiene la descripción original de una línea Dummy enviada por el bus.
 * Los campos descriptivos del documento tienen prioridad sobre ProductName,
 * que puede contener el nombre genérico recuperado posteriormente del PIM.
 */
export const getDummyLitaDescription = product =>
  product?.Description ||
  product?.ProductDescription ||
  product?.CustomDescription ||
  product?.ProductName ||
  i18n.t('DUMMY_LITA_T.DEFAULT_DESCRIPTION')

export const validateRoles = (userRoles, role) => {
  return userRoles.some(roleObject => roleObject.RoleName === role)
}

export const verifyContainsRoleKeyword = ({ userRoles, keywords }) => {
  return userRoles.some(roleObject =>
    keywords.some(keyword =>
      roleObject.RoleName.toLowerCase().includes(keyword.toLowerCase())
    )
  )
}

export const hasChangePriceApprove = productList => {
  const hasProductsDiscount = productList?.filter(
    product => product?.NegotiatedReasonId
  )

  return hasProductsDiscount.length > 0
    ? !hasProductsDiscount?.every(
        product => product?.AuthorizedPriceBy && product?.AuthorizedPriceByName
      )
    : false
}

export const needsPriceApproval = productList =>
  productList?.some(product => product?.NegotiatedReasonId) || false

export const getPriceApproverName = productList => {
  const approvedProduct = productList?.find(
    product => product?.NegotiatedReasonId && product?.AuthorizedPriceByName
  )

  return approvedProduct?.AuthorizedPriceByName || null
}
export const FormatMoney = (
  number,
  rounded = false,
  decimal = 2,
  options = {}
) => {
  const { withDecimal = true } = options

  if (!number) return '0.00'

  let parseNumber

  if (typeof number === 'string') {
    number = Number(number.replace(/,/g, ''))
  }
  if (rounded) {
    parseNumber =
      Math.round(number * Math.pow(10, decimal)) / Math.pow(10, decimal)
  } else {
    parseNumber =
      Math.round(number * Math.pow(10, decimal + 2)) / Math.pow(10, decimal + 2) // Redondear solo si rounded es true
  }

  const integerPart = Math.floor(parseNumber)

  let decimalPart = ''
  const decimalStr = parseNumber.toString().split('.')[1]
  if (decimalStr) {
    decimalPart = decimalStr.substring(0, Math.min(decimalStr.length, decimal))
  }

  // Si los decimales son todos ceros, mostrar solo la parte entera
  if (!withDecimal || (parseInt(decimalPart) === 0 && decimal > 0)) {
    return integerPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  const shouldPadTwo = decimalPart.length <= 1
  const shouldPadDecimal =
    (decimalPart.length === 1 && decimal === 2) ||
    (decimalPart.length === 3 && decimal === 4)

  const targetLength = shouldPadTwo
    ? 2
    : shouldPadDecimal
    ? decimal
    : decimalPart.length

  while (decimalPart.length < targetLength) {
    decimalPart += '0'
  }

  let formattedNumber

  if (decimalPart) {
    formattedNumber = `${integerPart
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`
  } else {
    formattedNumber = integerPart
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  return formattedNumber
}

export const searchCompanyName = validator => {
  const companies = {
    [companiesId.novey]: 'Novey',
    [companiesId.cochez]: 'Cochez',
    [companiesId.puntoCochez]: 'Punto Cochez',
  }
  const companyName = companies[validator.CompanyId.toUpperCase()] || 'N/A'

  return companyName
}

export const FormatMoneyTable = (
  number,
  rounded = false,
  decimal = 2,
  money
) => {
  if (
    number === null ||
    number === undefined ||
    isNaN(number) ||
    number === 0
  ) {
    if (money) {
      return '0.00'
    } else {
      return '0'
    }
  }
  let parseNumber

  if (rounded) {
    parseNumber =
      Math.round(number * Math.pow(10, decimal)) / Math.pow(10, decimal)
  } else {
    parseNumber =
      Math.round(number * Math.pow(10, decimal + 2)) / Math.pow(10, decimal + 2) // Redondear solo si rounded es true
  }

  const integerPart = Math.floor(parseNumber)

  let decimalPart = ''
  const decimalStr = parseNumber.toString().split('.')[1]
  if (decimalStr) {
    decimalPart = decimalStr.substring(0, Math.min(decimalStr.length, decimal))
  }

  // Si los decimales son todos ceros, mostrar solo la parte entera
  if (parseInt(decimalPart) === 0 && decimal > 0) {
    return integerPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  // Completar con ceros a la derecha si faltan decimales
  if (
    (decimalPart.length === 1 && decimal === 2) ||
    (decimalPart.length === 3 && decimal === 4)
  ) {
    while (decimalPart.length < decimal) {
      decimalPart += '0'
    }
  }

  let formattedNumber

  if (decimalPart) {
    formattedNumber = `${integerPart
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`
  } else {
    formattedNumber = integerPart
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  if (money) {
    if (formattedNumber.indexOf('.') === -1) {
      formattedNumber += '.00'
    } else {
      const decimalPart = formattedNumber.split('.')[1]

      if (decimalPart.length === 1) {
        formattedNumber += '0'
      }
    }
  }

  return formattedNumber
}

export const handleCloneQuote = async ({
  lines,
  setIsLoading = () => {},
  checkFinalPrice,
  customerId,
  history,
}) => {
  setIsLoading(true)

  try {
    const lineProducts = lines?.map(prod => {
      return {
        ...prod,
        qty: prod.Quantity,
      }
    })

    await checkFinalPrice({
      prods: lineProducts,
      customerId,
      isClone: true,
    })
  } catch (error) {
    console.error(error)
  } finally {
    history.push(ROUTES.cloneQuote)
  }

  setIsLoading(false)
}

export const FormatedDate = (date, time = true) => {
  if (!date) return ''

  const isISO = date.includes('T')
  let year, month, day, hours, minutes

  if (isISO) {
    const [fullDate, time] = date.split('T')
    ;[year, month, day] = fullDate.split('-')
    ;[hours, minutes] = time.split(':')
  } else {
    const [datePart, timePart] = date.split(' ')
    ;[year, month, day] = datePart.split(/[-/]/)
    ;[hours = '', minutes = ''] = timePart?.split(':') || []
  }

  const formattedDate = `${day.padStart(2, '0')}/${month.padStart(
    2,
    '0'
  )}/${year}`

  const formattedTime =
    hours && minutes
      ? `${parseInt(hours, 10) % 12 || 12}:${minutes.padStart(2, '0')} ${
          parseInt(hours, 10) >= 12 ? 'p. m.' : 'a. m.'
        }`
      : ''

  return formattedTime && time
    ? `${formattedDate} ${formattedTime}`
    : !time
    ? formattedDate
    : formattedDate
}

export const formatedDate = date => {
  const rawDate = date

  if (!rawDate) return ''

  const [datePart, timePart] = rawDate.split(' ')
  const [year, month, day] = datePart.split(/[-/]/)
  const [hours, minutes] = timePart?.split(':') || []

  let dateObj
  let formattedTime

  if (hours && minutes) {
    dateObj = new Date(`${year}-${month}-${day}T${hours}:${minutes}`)
    formattedTime = dateObj.toLocaleString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  } else {
    dateObj = new Date(`${year}-${month}-${day}`)
  }

  const formattedDate = dateObj.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  return hours && minutes ? `${formattedDate} ${formattedTime}` : formattedDate
}

export const fetchDocumentODV = async (hasOdv, useEstadoGuardar, user) => {
  const payload = {
    DocumentTypeId: hasOdv.DocumentTypeId,
    DocumentHeaderId: hasOdv.DocHeaderId,
    DocumentNumber: hasOdv.DocumentNumber,
    PageNumber: 1,
    PageSize: 1,
  }
  const dataODV = useEstadoGuardar?.QuoteHeader
  const respuesta = await searchSalesDocumentDetail(payload)
  const odvAllData = respuesta?.data?.Data
  const cotiAllData = dataODV || useEstadoGuardar
  if (odvAllData) {
    odvAllData.Amount = cotiAllData.Amount
    odvAllData.AmountWithTax = cotiAllData.AmountWithTax
    odvAllData.Tax = cotiAllData.Tax
    odvAllData.TaxAmount = cotiAllData.TaxAmount
    odvAllData.TaxExempt = cotiAllData.TaxExempt ?? false
  }
  const addSalesOrderLineId = (odvAllData?.Lines ?? []).map(prod => ({
    ...prod,
    SalesOrderLineId: prod.DocLineId,
  }))
  const odvProducts = addSalesOrderLineId
  const cotiProds = dataODV?.Lines || useEstadoGuardar?.Lines
  let availableOdvProducts = odvProducts ? [...odvProducts] : []
  const sameId = (firstId, secondId) =>
    firstId != null &&
    secondId != null &&
    String(firstId).toUpperCase() === String(secondId).toUpperCase()

  const findAndTakeOdvProduct = prod => {
    const quoteLineNum = Number(prod.LineNum)
    const isProdWarranty = prod.WarrantyProductId != null
    let foundProduct = availableOdvProducts.find(
      item =>
        prod.LineNum != null &&
        item.LineNum != null &&
        Number(item.LineNum) === quoteLineNum &&
        sameId(item.ProductId, prod.ProductId) &&
        isProdWarranty === (item.WarrantyProductId != null)
    )

    if (!foundProduct && isProdWarranty) {
      foundProduct = availableOdvProducts.find(
        item =>
          item.WarrantyProductId != null &&
          sameId(item.WarrantyProductId, prod.WarrantyProductId)
      )
      // fallback: ODV created before WarrantyProductId was populated
      if (!foundProduct) {
        foundProduct = availableOdvProducts.find(item =>
          sameId(item.ProductId, prod.ProductId)
        )
      }
    } else if (!foundProduct) {
      // backend returns empty GUID instead of null for regular ODV lines
      foundProduct = availableOdvProducts.find(item =>
        sameId(item.ProductId, prod.ProductId)
      )
    }

    if (foundProduct) {
      availableOdvProducts = availableOdvProducts.filter(
        item => item !== foundProduct
      )
    }

    return foundProduct
  }

  const getTaxCategoryValue = (prod, foundProduct) => {
    if (cotiAllData.TaxExempt) return null

    const taxCategoryValue =
      prod.TaxCategoryValue ?? foundProduct?.TaxCategoryValue
    if (taxCategoryValue != null) return taxCategoryValue

    const amount = Number(prod.Amount) || 0
    const tax = Number(prod.Tax) || 0
    return amount > 0 ? tax / amount : 0
  }

  const getTaxCategoryId = (prod, foundProduct) =>
    cotiAllData.TaxExempt
      ? null
      : prod.TaxCategoryId ?? foundProduct?.TaxCategoryId

  const getNegotiationFields = (prod, foundProduct) => {
    const shouldUpdateLineValues =
      !foundProduct ||
      Number(prod.Amount) !== Number(foundProduct.Amount) ||
      Number(prod.Price) !== Number(foundProduct.Price) ||
      Number(prod.Quantity) !== Number(foundProduct.Quantity)

    if (!shouldUpdateLineValues) {
      return {}
    }

    if (prod.Price < prod.FinalPrice) {
      return {
        AuthorizedPriceBy: null,
        AuthorizedPriceByName: null,
        NegotiatedPriceBy: user?.AuthenticationInfo?.UserId,
        NegotiatedPriceByName: user?.AuthenticationInfo?.UserName,
      }
    }

    return {
      NegotiatedPriceBy: null,
      NegotiatedPriceByName: null,
    }
  }

  const getSyncedOdvLine = (prod, foundProduct) => {
    const quantity = Math.ceil(Number(prod.Quantity) || 0)
    const baseLine = foundProduct || prod
    const odvLineIds = foundProduct
      ? {
          DocHeaderId: foundProduct.DocHeaderId,
          DocLineId: foundProduct.DocLineId,
          SalesOrderLineId: foundProduct.SalesOrderLineId,
        }
      : {
          DocHeaderId: hasOdv.DocHeaderId,
          DocLineId: '00000000-0000-0000-0000-000000000000',
          SalesOrderLineId: '00000000-0000-0000-0000-000000000000',
        }

    return {
      ...prod,
      ...baseLine,
      ...getNegotiationFields(prod, foundProduct),
      ...odvLineIds,
      // cotizacion lines may have null WarehouseId; use the ODV line/header warehouse instead
      WarehouseId:
        prod.WarehouseId ||
        foundProduct?.WarehouseId ||
        odvAllData?.WarehouseId,
      // dispatch validates LineNum against ODV lines
      LineNum: foundProduct?.LineNum ?? prod.LineNum,
      // cotizador rápido lines may lack PriceListId; preserve ODV's value
      PriceListId: foundProduct?.PriceListId ?? prod.PriceListId,
      Amount: prod.Amount,
      AmountWithTax: prod.AmountWithTax,
      DiscountAmount: prod.DiscountAmount,
      Tax: prod.Tax,
      TaxCategoryId: getTaxCategoryId(prod, foundProduct),
      TaxCategoryValue: getTaxCategoryValue(prod, foundProduct),
      Price: prod.Price,
      Quantity: quantity,
      QtyCanceled: foundProduct?.QtyCanceled || 0,
      QtyDelivered: foundProduct?.QtyDelivered || 0,
      QtyDeliveredCalculated: foundProduct?.QtyDeliveredCalculated || 0,
      QtyOutstanding: foundProduct?.QtyOutstanding || 0,
      QtyPending: foundProduct?.QtyPending || 0,
      QtyOrdered: quantity,
    }
  }

  const productCheck = cotiProds?.map(prod => {
    const foundProduct = findAndTakeOdvProduct(prod)

    if (!foundProduct) {
      return getSyncedOdvLine(prod, null)
    }

    return getSyncedOdvLine(prod, foundProduct)
  })
  const isCountedCustomer = dataODV && validatorIsCountedCustomer(dataODV)
  const countedCustomerPhone =
    dataODV?.Telephone_Number ||
    dataODV?.Cellphone_Number ||
    dataODV?.Telephone ||
    null
  const odvSavePayload = {
    DocumentTypeId: hasOdv.DocumentTypeId,
    SalesOrder: {
      SalesOrderHeaderId: hasOdv.DocHeaderId,
      ...odvAllData,
      WarehouseAddress: dataODV?.WarehouseAddress,
      WarehouseCode: dataODV?.WarehouseCode,
      WarehouseDescription: dataODV?.WarehouseDescription,
      WarehouseId: dataODV?.WarehouseId,
      SalesAgentId: dataODV?.SalesAgentId,
      SalesAgentFullName: dataODV?.SalesAgentFullName,
      CustomerId: dataODV?.CustomerId || CUSTOMERID,
      CustomerNameDocument: dataODV?.CustomerNameDocument,
      CustomerEmail: dataODV?.CustomerEmail,
      // Replace the contact info inherited from the ODV (e.g. from a previous
      // registered customer) with only what the cash customer has; a phone
      // that was not entered is sent as null so the stored one is cleared
      ...(isCountedCustomer && {
        CustomerFullName: dataODV?.CustomerNameDocument || '',
        CustomerEmail: dataODV?.CustomerEmail || '',
        EmailAddress: dataODV?.CustomerEmail || '',
        Telephone: countedCustomerPhone,
        Telephone_Number: countedCustomerPhone,
        Cellphone_Number: countedCustomerPhone,
      }),
      Address_AddressDetail: dataODV?.Address_AddressId,
      Address_AddressId: dataODV?.Address_AddressId,
      AddressId: dataODV?.AddressId,
      Lines: productCheck,
    },
  }
  return await addUpdateSalesDocuments(odvSavePayload)
}

export const validatorErrorUpdate = async (
  hasOdv,
  quote,
  user,
  setIsErrorAddressModal,
  setMessageAddressModal,
  props,
  updatedQuote,
  action = 'reload'
) => {
  const response = await fetchDocumentODV(hasOdv, quote, user)
  const dataQuote = response?.data

  const hasError = !dataQuote?.Data || dataQuote?.Status?.Code !== 200

  if (hasError) {
    setIsErrorAddressModal(true)
    setMessageAddressModal({
      message: dataQuote?.Status?.Message ?? 'Error desconocido',
      status: 'error',
    })
    setIsErrorAddressModal(true)
    setMessageAddressModal({
      message: response?.data?.Status?.Message,
      status: 'error',
    })

    setTimeout(() => {
      if (action === 'editionPage') {
        props.history.push(
          `${ROUTES.editQuoteHeaderId}${quote?.QuoteHeader?.DocHeaderId}/${quote?.QuoteHeader?.DocumentNumber}`
        )
      }

      if (action === 'reload') {
        window.location.reload()
      }
    }, 3000)
    return true
  }
  return false
}

export const formatterCurrency = function (value) {
  // check if value is a number or string parse it to float
  if (typeof value === 'string') {
    value = parseFloat(value)
  }
  if (value) {
    return value.toFixed(2).toString().replace(constFormatterCurrency, ',')
  }
  if (value === 0) {
    return '0.00'
  }
  return value
}

export const toTitleCase = str => {
  const data = str?.replace(searchTitle, function (txt) {
    return txt?.charAt(0).toUpperCase() + txt?.substr(1).toLowerCase()
  })
  return data
}

export const formatDate = dateString => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear().toString()
  const hours = date.getHours() > 12 ? date.getHours() - 12 : date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const amPm = date.getHours() >= 12 ? 'pm' : 'am'

  return `${day}/${month}/${year} ${hours}:${minutes} ${amPm}`
}

/**
 * `getMarginTotal` takes in a price, cost, and quantity and returns the total margin
 * @param price - the price of the item
 * @param cost - the cost of the item
 * @param qty - the quantity of the item
 */

const formula = (totalPrice, totalCost) => {
  const totalMargin = ((totalPrice - totalCost) / totalPrice) * 100
  return totalMargin
}
export const getMarginTotal = (price = 0, cost = 0, qty = 1) => {
  const totalPrice = price * qty
  const totalCost = cost * qty
  return formula(totalPrice, totalCost)
}

export const marginTotal = (costs, total) => {
  return formula(total, costs)
}

export const isCallCenterUser = userRoles => {
  return userRoles.some(
    roleObject => roleObject.RoleName === salesRoles.callCenter
  )
}

export const isGerentsStore = userRoles => {
  return userRoles.some(
    roleObject =>
      roleObject.RoleName ===
      (salesRoles.gerenteTienda || salesRoles.supervisorTienda)
  )
}

export const generateColorStatus = (
  status,
  pendingStatusDocument,
  processStatusDocument,
  closeStatusDocument,
  expiredStatusDocument,
  colorsStatus
) => {
  const statusMappings = {
    pending: pendingStatusDocument,
    process: processStatusDocument,
    close: closeStatusDocument,
    expired: expiredStatusDocument,
  }

  for (const color in statusMappings) {
    if (Object.values(statusMappings[color]).includes(status)) {
      return colorsStatus[color]
    }
  }

  return colorsStatus.default
}

export const refreshShoppingCart = async ({ cart, customerId, dispatch }) => {
  if (cart.length) {
    const productList = cart
    dispatch(emptyCart())
    const productIds = productList.map(product => product.ProductId)
    const inventoryProducts = await dispatch(fetchPriceInventory(productIds))

    const newProductList = productList.map(product => {
      const productInCart = inventoryProducts.find(
        productCart => productCart.ProductId === product.ProductId
      )
      return {
        ...product,
        Cost: productInCart?.Cost || productInCart?.AvgCost || 0.0,
      }
    })
    dispatch(priceListAddToCart(newProductList, customerId))
  }
}
export const parseNumber = value => {
  if (typeof value === 'string') {
    return Number(value.replace(/,/g, ''))
  }
  return Number(value)
}

export const valitadorODCPDF = async document => {
  try {
    const odcDocument = await listODCDocument({
      documentTypeId: document?.DocumentTypeId,
      documentId: document?.DocHeaderId,
    })
    const pdfUrl = odcDocument?.data?.Data?.find(
      item => item?.DocumentUrl !== null
    )?.DocumentUrl

    if (!pdfUrl) return

    return odcDocument?.data?.Data
  } catch (error) {
    console.log('valitadorODCPDF -->', error)
  }
}

function mergeTints(existing = [], incoming = []) {
  const map = new Map()

  existing.forEach(t => {
    map.set(t.VariantDetailId, t)
  })
  incoming.forEach(t => {
    map.set(t.VariantDetailId, t)
  })
  return Array.from(map.values())
}

export async function validateRepeatedProduct(
  product,
  cart,
  dispatch,
  customerId,
  addWarranty,
  selectedWarranty
) {
  const requestedQty = Math.max(1, Number(product.Quantity || product.qty || 1))

  // Helper to mark discount
  const markDiscount = p => {
    if (p.BasePrice > (p.FinalPrice ?? p.Price ?? p.BasePrice)) {
      p.hasDiscount = true
    }
    p.FulfillmentPlanId = null
    p.FulfillmentPlanDescription = null
    return p
  }

  // Find existing product line by ProductId (SKU), excluding warranty lines
  const existingProductIndex = cart.findIndex(
    item => item.ProductId === product.ProductId && !item.IsExtendedWarranty
  )
  const existingProduct =
    existingProductIndex !== -1 ? cart[existingProductIndex] : null

  if (existingProduct) {
    // Merge quantity with existing product line
    const [integerPart, decimalPart] =
      existingProduct.Quantity.toString().split('.')
    const incrementedInteger =
      parseInt(integerPart || 0, 10) + parseInt(requestedQty, 10)
    const newQuantity = decimalPart
      ? `${incrementedInteger}.${decimalPart}`
      : `${incrementedInteger}`

    // Determine existing warranty before dispatching so we can set WarrantyYears
    // on the parent product when a new warranty is being added
    const existingWarranty = selectedWarranty?.WarrantyYears
      ? cart.find(
          item =>
            item.IsExtendedWarranty &&
            item.WarrantyProductId === existingProduct.ProductId
        )
      : null

    const warrantyYears =
      selectedWarranty?.WarrantyYears &&
      (!existingWarranty ||
        existingWarranty.WarrantyYears !== selectedWarranty.WarrantyYears)
        ? selectedWarranty.WarrantyYears
        : undefined

    const updatedProduct = {
      ...existingProduct,
      Quantity: newQuantity,
      qty: newQuantity,
      ColorVariants: mergeTints(
        existingProduct.ColorVariants ?? [],
        product.ColorVariants ?? []
      ),
      ...(warrantyYears !== undefined ? { WarrantyYears: warrantyYears } : {}),
    }
    await dispatch(priceListAddToCart([updatedProduct], customerId))

    // Handle warranty for merged product
    if (selectedWarranty?.WarrantyYears) {
      if (!existingWarranty) {
        // No existing warranty: add warranty for the newly added units
        await addWarranty({
          selectedWarranty,
          productId: existingProduct.ProductId,
          quantity: requestedQty,
        })
      } else if (
        existingWarranty.WarrantyYears === selectedWarranty.WarrantyYears
      ) {
        // Same warranty type: increment warranty qty by requestedQty (capped at product qty)
        await addWarranty({
          selectedWarranty,
          productId: existingProduct.ProductId,
          quantity: Math.min(
            Number(existingWarranty.Quantity) + requestedQty,
            parseInt(newQuantity, 10)
          ),
        })
      } else {
        // Different warranty type: replace existing warranty with new type
        // New warranty quantity = only the newly added units
        await addWarranty({
          selectedWarranty,
          productId: existingProduct.ProductId,
          quantity: requestedQty,
        })
      }
    }

    return true
  }

  // Product doesn't exist yet: create a single new line with requestedQty
  const newLineNum = getNextLineNum(cart)
  const newProduct = markDiscount({
    ...product,
    LineNum: newLineNum,
    Quantity: requestedQty,
    qty: requestedQty,
    ...(selectedWarranty?.WarrantyYears
      ? { WarrantyYears: selectedWarranty.WarrantyYears }
      : {}),
  })

  await dispatch(priceListAddToCart([newProduct], customerId))

  if (selectedWarranty?.WarrantyYears) {
    await addWarranty({
      selectedWarranty,
      productId: newProduct.ProductId,
      quantity: requestedQty,
    })
  }

  return true
}

export const updateHiddenColumns = (newColumns, columns) => {
  // eslint-disable-next-line array-callback-return
  const enabledHiddenColumns = columns.filter(column => {
    if (column.hideEnabled) {
      return column.Header
    }
  })
  // if newColumns is empty, then show all columns false
  const newVisibleColumns = enabledHiddenColumns.map(column => {
    return {
      ColumnName: column.Header,
      Status: !!newColumns.includes(column.Header),
    }
  })
  return newVisibleColumns
}

/**
 * The function takes a date input and returns it in the format yyyy-mm-dd.
 * @param date - The `date` parameter is a string or a Date object representing the date for which you
 * want to get the formatted date string in the format "yyyy-mm-dd".
 * @returns The function `getDateFormatted` returns a string representing the current date in the
 * format "yyyy-mm-dd".
 */
export const getDateFormatted = date => {
  // get current date in format yyyy-mm-dd
  const today = new Date(date)
  const dd = String(today.getDate()).padStart(2, '0')
  const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
  const yyyy = today.getFullYear()
  return yyyy + '-' + mm + '-' + dd
}

export const downloadPdfFuntion = async (
  Whatsapp = false,
  header,
  dispatch,
  setShowFailMessageModal,
  documentType,
  isCatalog = false
) => {
  const payload = {
    DocumentTypeId:
      documentType ||
      (isCatalog ? documentTypeId.CATALOG : documentTypeId.QUOTATION),
    DocHeaderId: header,
  }
  try {
    const Data = await dispatch(fetchQuotePDF(payload))
    if (Data && Data?.BlobResponse) {
      if (Whatsapp) {
        // open Api whatsapp with url
        openUrlInNewTab(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(
            Data?.BlobResponse?.FileUrl
          )}`
        )
      } else {
        openUrlInNewTab(Data?.BlobResponse?.FileUrl)
      }
    } else {
      throw new Error(Data?.toString())
    }
  } catch (error) {
    console.log('ERRORASO: ', error)
    setShowFailMessageModal(true)
    setShowFailMessageModal(true)
  }
}

export const generateRegularExpression = (decimalLimit, RegExp) => {
  return new RegExp(`^\\d{1,3}(,\\d{3})*(\.\\d{1,${decimalLimit}})?$`)
}

export const handleFilterList = ({
  list,
  setFilterList,
  mapKey,
  setCurrentPage,
  setLastIndex,
  setFirstIndex,
}) => {
  if (!list) {
    setFilterList([])
  } else {
    const mappedList = list.map(option => ({ [mapKey]: option }))
    setFilterList(mappedList)
  }

  setCurrentPage(1)
  setLastIndex(3)
  setFirstIndex(0)
}

export const handlePriceInventory = async ({
  hits,
  customerId,
  warehouse,
  companyCode,
  companyId,
  getPrices,
  t,
}) => {
  const PriceInventoryData = {
    CompanyId: companyId,
    CompanyCode: companyCode,
    WarehouseId: warehouse,
    ProductIds: hits?.map(item => item.productId || item.ProductId) || [],
    PriceListId: null,
    UoMId: null,
  }

  try {
    const priceData = await getPrices(PriceInventoryData)

    const priceDataMap = {}
    priceData.forEach(product => {
      priceDataMap[product.ProductId] = product
    })

    const productArrayWithPrice = hits.map(item => ({
      ...item,
      ...priceDataMap[item.productId || item.ProductId],
    }))

    return productArrayWithPrice
  } catch (error) {
    throw (t('PRODUCTS_SEARCH_T.ERROR_FILTERING_PRICES_LABEL'), error)
  }
}

export const validatorIsCountedCustomer = customer => {
  return (
    customer?.CustomerId?.toUpperCase() === CUSTOMERID || !customer?.CustomerId
  )
}

export const handleAddProductToCart = async ({
  product,
  cotizacion,
  isEditCoti,
  setCloseWarningModal,
  setInactiveMessage,
  cart,
  dispatch,
  customerId,
  setShowAlertDontAddProduct,
  setShowMessage,
  setShowNetworkErrorAlert,
  setApiErrorMessage,
}) => {
  const cost =
    product?.Cost === 0 || product?.Cost === null
      ? product?.AvgCost
      : product?.Cost

  if (isEditCoti && cotizacion.DocHeaderStatusAction === 'OPEN') {
    if (
      !product.IsService &&
      !isCostExemptProduct(product) &&
      (cost === 0 || cost === null)
    ) {
      setCloseWarningModal?.(true)
      return false
    } else {
      setCloseWarningModal?.(false)
    }
  }

  if (!product?.IsActive) {
    setInactiveMessage?.(true)
    setTimeout(() => setInactiveMessage?.(false), 3000)
    return false
  }

  if (product?.FinalPrice !== undefined && product?.FinalPrice !== null) {
    const isOffline = !navigator.onLine
    const alreadyInCart = cart?.some(
      item => item.ProductId === product.ProductId
    )

    if (isOffline && !alreadyInCart) {
      setShowNetworkErrorAlert?.(true)
      setTimeout(() => setShowNetworkErrorAlert?.(false), 4000)
      return false
    }

    try {
      const added = await validateRepeatedProduct(
        product,
        cart,
        dispatch,
        customerId
      )

      if (!added) {
        setShowAlertDontAddProduct?.(true)
        setTimeout(() => setShowAlertDontAddProduct?.(false), 2000)
        return false
      }

      return true
    } catch (_err) {
      const isNetworkError = !navigator.onLine || !_err?.response
      if (isNetworkError) {
        setShowNetworkErrorAlert?.(true)
        setTimeout(() => setShowNetworkErrorAlert?.(false), 4000)
      } else {
        const apiMessage =
          _err?.response?.data?.Status?.Message ||
          _err?.response?.data?.message ||
          ''
        setApiErrorMessage?.(apiMessage)
        setTimeout(() => setApiErrorMessage?.(null), 4000)
      }
      return false
    }
  } else {
    setShowMessage?.(true)
    setTimeout(() => setShowMessage?.(false), 3000)
    return false
  }
}

/**
 * Limpia un texto eliminando espacios innecesarios.
 *
 * - Elimina espacios en blanco al inicio y al final del texto.
 * - Reemplaza múltiples espacios consecutivos (incluyendo tabs y saltos de línea) por un solo espacio.
 * - Asegura que el texto sea seguro para guardar o mostrar de forma limpia.
 *
 * @param text - Texto a limpiar. Puede ser undefined o string.
 * @returns El texto limpiado con un solo espacio entre palabras.
 */
export const sanitizeText = text => {
  return (text || '').trim().replace(/\s+/g, ' ')
}

/**
 * Sanitiza el número de orden de compra eliminando caracteres no permitidos.
 * Solo permite: letras (a-z, A-Z), números (0-9), guiones (-), guiones bajos (_),
 * barras (/) y espacios válidos entre texto.
 *
 * @param text - Texto a limpiar.
 * @returns El texto limpiado con solo caracteres permitidos.
 */
export const sanitizeOrderNumber = text => {
  if (!text) return ''
  return (
    text
      .replace(/\\u[\da-fA-F]{4}/g, '')
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[^a-zA-Z0-9\s\-_/]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  )
}

export const sanitizeCellPhone = value => {
  if (value == null) return ''
  return String(value).replace(/\D/g, '').slice(0, 8)
}
